import { Injectable } from '@angular/core';
import { ChatMessageMediaModel, ChatMessageModel } from '@app/model/chat-message.model';
import { ChatMessageAdapter } from '@app/model/chat-message-adapter.model';
import { BundleType, ChatMessageType, EntityTypeId, FieldName, FileModel, ImageModel, MediaPresignApiService } from 'library-explorer';
import { Observable, combineLatest, map, mergeMap, of, shareReplay } from 'rxjs';
import { MediaApiService } from '@app/services/api/media-api.service';

@Injectable()
export class SendBirdChatAdapter implements ChatMessageAdapter {

  constructor(
    private readonly mediaApiService: MediaApiService,
    private readonly mediaPresignApiService: MediaPresignApiService
  ) {}

  public mapMessage(message: SendBird.UserMessage | SendBird.FileMessage): ChatMessageModel {
    const messageData = message.data ? JSON.parse(message.data) : null;

    return {
      id: message.messageId.toString(),
      message: (message as SendBird.UserMessage).message || "",
      timestamp: message.createdAt,
      sender: {
        fullname: message.sender?.nickname || "Admin",
        mediaImage: message.sender?.profileUrl && { url: message.sender?.profileUrl } as ImageModel,
        avatarUrl: message.sender?.profileUrl,
        metadata: message.sender?.metaData,
      },
      type: this.mapMessageType(message),
      media: this.mapMedia(message, messageData),
      isFileMessage: message.messageType !== "user" || !!message.customType,
    };
  }

  private mapMessageType(msg: SendBird.UserMessage | SendBird.FileMessage): ChatMessageType|string {
    if (!msg.customType && msg.messageType === 'file') {
      if (msg.type?.startsWith('image/')) return ChatMessageType.IMAGE_MESSAGE;
      return ChatMessageType.FILE_MESSAGE;
    }

    return msg.customType || ChatMessageType.TEXT_MESSAGE;
  }

  private mapMedia(msg: any, data: any): ChatMessageMediaModel | undefined {
    if (!data && msg.messageType !== 'file') return undefined;

    return {
      url: msg.url,
      mediaId: data?.mediaId,
      filename: data?.filename || data?.name || msg.name,
      mimeType: msg.type,
      options: data?.options,
      file$: this.mapMediaFile(msg, data),
    };
  }

  private mapMediaFile(msg: any, data: any): Observable<FileModel & { presignedUrl: string } | null> {
    if (!data) {
      return;
    }

    return this.mediaApiService.getMediaEntity(data.mediaId).pipe(
      mergeMap((media) => {
        if (!media) {
          return of(null);
        }

        if (msg.customType === ChatMessageType.VIDEO_MESSAGE) {
          return of({
            ...media,
            presignedUrl: media.stream || media.url || media.uri,
          });
        }

        const fieldName =
          msg.customType === ChatMessageType.IMAGE_MESSAGE
            ? FieldName.MEDIA_IMAGE
            : FieldName.MEDIA_FILE;

        return combineLatest([
          of(media),
          this.mediaPresignApiService.getPreSignedUrl(
            EntityTypeId.CHAT_CHANNEL,
            BundleType.CHAT_CHANNEL,
            fieldName,
            media,
          ),
        ]).pipe(map(([media, presignedUrl]) => ({ ...media, presignedUrl })));
      }),
      shareReplay({bufferSize: 1, refCount: true })
    );
  }
}
